import { useCallback, useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, ShoppingBag, Receipt, TrendingUp } from "lucide-react";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const STATUS_LABEL = {
  new: "New",
  in_progress: "In Progress",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function ReportPanel() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/report/daily", { params: { date: d } });
      setData(res.data);
    } catch (e) {
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const Stat = ({ icon: Icon, label, value, tid }) => (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={tid}>
      <div className="flex items-center gap-2 text-stone-500">
        <Icon className="h-4 w-4 text-orange-700" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold text-stone-900">{value}</div>
    </div>
  );

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-stone-900">Daily Sales Report</h2>
          <p className="text-sm text-stone-600">{data?.date_display || "\u00a0"}</p>
        </div>
        <div>
          <Label htmlFor="report-date" className="text-sm font-semibold">Date</Label>
          <Input
            id="report-date"
            data-testid="report-date-input"
            type="date"
            value={date}
            max={todayStr()}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1.5 w-48"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-stone-500">Loading…</p>
      ) : !data ? (
        <p className="rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center text-stone-500">No report available.</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={DollarSign} label="Total Revenue" value={money(data.revenue)} tid="report-revenue" />
            <Stat icon={ShoppingBag} label="Orders" value={data.order_count} tid="report-order-count" />
            <Stat icon={TrendingUp} label="Avg Order" value={money(data.avg_order)} tid="report-avg-order" />
            <Stat icon={Receipt} label="Tax Collected" value={money(data.tax)} tid="report-tax" />
          </div>

          {data.order_count === 0 ? (
            <p className="mt-6 rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center text-stone-500">
              No paid orders on this day yet.
            </p>
          ) : (
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-stone-200 bg-white p-6">
                <h3 className="font-display text-lg font-bold text-stone-900">Top Items</h3>
                <div className="mt-4 space-y-2">
                  {data.top_items.map((it) => (
                    <div key={it.name} className="flex items-center justify-between border-b border-stone-100 pb-2 text-sm">
                      <span className="text-stone-700"><span className="font-semibold text-stone-900">{it.quantity}×</span> {it.name}</span>
                      <span className="font-semibold text-stone-900">{money(it.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-white p-6">
                <h3 className="font-display text-lg font-bold text-stone-900">Orders by Status</h3>
                <div className="mt-4 space-y-2">
                  {Object.entries(data.by_status).map(([s, n]) => (
                    <div key={s} className="flex items-center justify-between border-b border-stone-100 pb-2 text-sm">
                      <span className="text-stone-700">{STATUS_LABEL[s] || s}</span>
                      <span className="font-semibold text-stone-900">{n}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-between border-t border-stone-200 pt-3 text-sm">
                  <span className="text-stone-600">Subtotal (excl. tax)</span>
                  <span className="font-bold text-stone-900">{money(data.subtotal)}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
